import { closeChecklistURLs } from 'routes/close/closeURLs';

import { CommentNotificationDisplay } from 'components/comments/CommentNotificationDisplay';
import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';

import {
  TaskCommentReplyAddedNotification,
  TaskCommentReplyEditedNotification,
  TaskCommentReplyTaggedNotification,
} from 'types/inbox-notifications/close/ChecklistNotifications';
import { SegmentAtByDot } from 'types/utils';

export type TaskCommentReplyNotification =
  | TaskCommentReplyAddedNotification
  | TaskCommentReplyEditedNotification
  | TaskCommentReplyTaggedNotification;
type TaskCommentReplyLevelKeys = SegmentAtByDot<
  TaskCommentReplyNotification['type'],
  5
>;
export function CommentReplyNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: TaskCommentReplyNotification;
}) {
  const [actionType, ..._restOfType] = type;

  switch (actionType as TaskCommentReplyLevelKeys) {
    case 'added':
      return (
        <ReplyAddedNotificationItem
          item={item as TaskCommentReplyAddedNotification}
        />
      );
    case 'edited':
      return (
        <ReplyEditedNotificationItem
          item={item as TaskCommentReplyEditedNotification}
        />
      );
    case 'tagged':
      return (
        <ReplyTaggedNotificationItem
          item={item as TaskCommentReplyTaggedNotification}
        />
      );
    default:
      return (
        <div>Unknown task comment reply notification type: {actionType}</div>
      );
  }
}

function ReplyAddedNotificationItem({
  item,
}: {
  item: TaskCommentReplyAddedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply) {
    return null;
  }

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} replied to a comment.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}

function ReplyEditedNotificationItem({
  item,
}: {
  item: TaskCommentReplyEditedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply) {
    return null;
  }

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} updated a reply.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}

function ReplyTaggedNotificationItem({
  item,
}: {
  item: TaskCommentReplyTaggedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply) {
    return null;
  }

  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} tagged you in a reply.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}
